import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { SAVE_RETURN_CUSTOMER_RESTRICTIONS } from 'gql/mutations';
import {
  GET_RETURN_CUSTOMER_RESTRICTIONS,
  GET_RETURN_CUSTOMER_RESTRICTION_ORDER,
} from 'gql/queries';
import {
  normalizeReturnCustomerTags,
  normalizeReturnCustomerEmails,
  normalizeReturnBlockedCustomers,
  validReturnBlockedCustomer,
  getReturnCustomerRestrictionErrorCode,
  ReturnBlockedCustomer,
  ReturnCustomerRestrictions,
} from 'types/returnCustomerRestrictions';

const emptySettings = (storeId: string): ReturnCustomerRestrictions => ({
  storeId,
  enabled: false,
  customerTags: [],
  blockedEmails: [],
  blockedCustomers: [],
});

const validSettings = (
  value: any,
  storeId: string,
): value is ReturnCustomerRestrictions =>
  value?.storeId === storeId &&
  typeof value.enabled === 'boolean' &&
  Array.isArray(value.customerTags) &&
  value.customerTags.every((tag: unknown) => typeof tag === 'string') &&
  Array.isArray(value.blockedEmails) &&
  value.blockedEmails.every((email: unknown) => typeof email === 'string') &&
  Array.isArray(value.blockedCustomers) &&
  value.blockedCustomers.every(validReturnBlockedCustomer);

const normalizeSettings = (
  value: ReturnCustomerRestrictions,
): ReturnCustomerRestrictions => ({
  storeId: value.storeId,
  enabled: value.enabled,
  customerTags: normalizeReturnCustomerTags(value.customerTags),
  blockedEmails: normalizeReturnCustomerEmails(value.blockedEmails),
  blockedCustomers: normalizeReturnBlockedCustomers(value.blockedCustomers),
});

export const useReturnCustomerRestrictions = (storeId = '', active = true) => {
  const {
    data,
    loading,
    error: queryError,
    refetch,
    client,
  } = useQuery(GET_RETURN_CUSTOMER_RESTRICTIONS, {
    variables: { storeId },
    skip: !active || !storeId,
    fetchPolicy: 'network-only',
  });
  const [mutate] = useMutation(SAVE_RETURN_CUSTOMER_RESTRICTIONS);
  const [state, setState] = useState({
    value: emptySettings(storeId),
    baseline: '',
    dirty: false,
  });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const selection = useRef({ storeId, generation: 0 });
  const mounted = useRef(true);
  const pending = useRef(false);
  useLayoutEffect(() => {
    if (selection.current.storeId !== storeId) {
      // Count committed switches so A -> B -> A also invalidates old saves.
      selection.current = {
        storeId,
        generation: selection.current.generation + 1,
      };
    }
  }, [storeId]);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  const serverValue = data?.returnCustomerRestrictions;
  const serverKey = validSettings(serverValue, storeId)
    ? JSON.stringify(normalizeSettings(serverValue))
    : '';

  useEffect(() => {
    setSaveError(null);
    setSaving(false);
    pending.current = false;
  }, [storeId]);

  useEffect(() => {
    setState((current) => {
      if (
        current.value.storeId === storeId &&
        (current.dirty || current.baseline === serverKey)
      )
        return current;
      return {
        value: serverKey ? JSON.parse(serverKey) : emptySettings(storeId),
        baseline: serverKey,
        dirty: false,
      };
    });
  }, [storeId, serverKey]);

  const value =
    state.value.storeId === storeId ? state.value : emptySettings(storeId);
  const dirty = active && state.value.storeId === storeId && state.dirty;
  const loaded = Boolean(
    active &&
      storeId &&
      serverKey &&
      state.value.storeId === storeId &&
      !loading &&
      !queryError,
  );
  const error =
    saveError ||
    (active && !loading && (!serverKey || queryError)
      ? 'Customer restrictions could not be loaded. Retry before making changes.'
      : null);

  const onChange = (next: ReturnCustomerRestrictions) => {
    if (!loaded || saving) return;
    const normalized = normalizeSettings({ ...next, storeId });
    setSaveError(null);
    setState((current) => ({
      ...current,
      value: normalized,
      dirty: JSON.stringify(normalized) !== current.baseline,
    }));
  };

  const save = async (): Promise<boolean> => {
    if (!dirty) return true;
    if (!loaded || pending.current) return false;
    const generation = selection.current.generation;
    pending.current = true;
    setSaving(true);
    setSaveError(null);
    try {
      const result = await mutate({
        variables: {
          input: {
            storeId,
            enabled: value.enabled,
            customerTags: value.customerTags,
            blockedEmails: value.blockedEmails,
            blockedOrderIds: value.blockedCustomers.map(
              (customer) => customer.orderId,
            ),
            blockedCustomerChecks: value.blockedCustomers.map((customer) => ({
              orderId: customer.orderId,
              customerId: customer.customerId,
              email: customer.email,
            })),
          },
        },
      });
      if (!mounted.current || generation !== selection.current.generation)
        return false;
      const saved = result.data?.saveReturnCustomerRestrictions;
      if (!validSettings(saved, storeId))
        throw new Error('Invalid settings response');
      const normalized = normalizeSettings(saved);
      setState({
        value: normalized,
        baseline: JSON.stringify(normalized),
        dirty: false,
      });
      return true;
    } catch (error) {
      if (mounted.current && generation === selection.current.generation) {
        setSaveError(
          getReturnCustomerRestrictionErrorCode(error) ===
            'RETURN_CUSTOMER_IDENTITY_CHANGED'
            ? 'A customer changed since lookup. Remove that customer and look up the order again before saving.'
            : 'Customer restrictions could not be saved. Try again.',
        );
      }
      return false;
    } finally {
      if (mounted.current && generation === selection.current.generation) {
        pending.current = false;
        setSaving(false);
      }
    }
  };

  const onLookupOrder = async (
    orderNumber: string,
  ): Promise<ReturnBlockedCustomer> => {
    if (!loaded || saving || !value.enabled)
      throw new Error('Restrictions unavailable');
    const generation = selection.current.generation;
    const result = await client.query({
      query: GET_RETURN_CUSTOMER_RESTRICTION_ORDER,
      variables: { storeId, orderNumber: orderNumber.trim() },
      fetchPolicy: 'no-cache',
    });
    const customer = result.data?.returnCustomerRestrictionOrder;
    if (
      !mounted.current ||
      generation !== selection.current.generation ||
      !validReturnBlockedCustomer(customer)
    ) {
      throw new Error('Customer lookup unavailable');
    }
    return normalizeReturnBlockedCustomers([customer])[0];
  };

  return {
    value,
    onChange,
    dirty,
    loaded,
    loading,
    saving,
    error,
    save,
    onLookupOrder,
    onRetry: () => refetch?.().catch(() => undefined),
  };
};
